import { useEffect, useId, useState } from "react";
import {
  LEAD_STATUS_GUIDE,
  LEAD_STATUS_ORDER,
  describeLeadStatus,
} from "../../features/admin/leadStatusGuide";

/** Status pill with a hover/screen-reader explanation. Unknown values show raw. */
export const LeadStatusPill: React.FC<{ status: string | null | undefined }> = ({ status }) => {
  const d = describeLeadStatus(status);
  return (
    <span
      className={`px-2 py-0.5 rounded-md text-xs font-medium ${d.pillClass}`}
      title={d.tooltip}
      aria-label={d.tooltip}
      data-testid="lead-status-pill"
    >
      {d.label}
    </span>
  );
};

/** Collapsible "Status guide" legend explaining every enquiry status. */
const LeadStatusGuide: React.FC = () => {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:text-white border border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-colors"
      >
        <span aria-hidden="true">ⓘ</span> Status guide
      </button>
      {open && (
        <div
          id={panelId}
          role="region"
          aria-label="Enquiry status guide"
          className="mt-2 bg-gray-900 border border-gray-800 rounded-xl p-4 text-sm"
        >
          <dl className="space-y-3">
            {LEAD_STATUS_ORDER.map((key) => {
              const info = LEAD_STATUS_GUIDE[key];
              return (
                <div key={key} className="flex flex-col gap-1 sm:flex-row sm:gap-4">
                  <dt className="sm:w-28 shrink-0">
                    <LeadStatusPill status={key} />
                  </dt>
                  <dd className="text-gray-300">
                    {info.meaning}
                    <span className="block text-xs text-gray-500 mt-0.5">{info.next}</span>
                  </dd>
                </div>
              );
            })}
          </dl>
          <p className="mt-3 pt-3 border-t border-gray-800 text-xs text-gray-500">
            The status in this list is the enquiry's shared status. An enquiry sent to several
            sellers also keeps one status per seller, and each seller sees their own. Any of those
            sellers accepting sets the shared status to Accepted; only the move to Contacted
            follows the deal owner (primary seller). Enquiries opened straight from a deal's
            "Get Details" chat start as Accepted because the chat is already live. The list is
            ordered by last update, newest first: accepting, replying or closing moves an enquiry
            to the top.
          </p>
        </div>
      )}
    </div>
  );
};

export default LeadStatusGuide;
